import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import ThrottleBar from "../components/ThrottleBar";
import DecadeBrowser from "../components/DecadeBrowser";

const STATUS_LABEL = {
  searching: "Searching…",
  queued: "Queued",
  downloading: "Downloading",
  "partially downloaded": "Partially downloaded",
  downloaded: "Downloaded",
  completed: "Downloaded",
  delay: "Waiting",
  warning: "Stalled",
  failed: "Failed",
  downloadclientunavailable: "Download client unavailable",
  fallback: "Using fallback release",
  unknown: "",
};

// Warning/failed states get an amber/red treatment instead of the default
// muted gray, since these actually need the user's attention.
const STATUS_COLOR = {
  warning: "text-amber-400",
  failed: "text-red-400",
  downloadclientunavailable: "text-red-400",
};

function LiveStatus({ requestId, onState }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/requests/${requestId}/status`);
        if (!cancelled) { setStatus(data); onState && onState(requestId, data.state); }
      } catch { /* transient — next poll will retry */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!status) return null;
  const label = STATUS_LABEL[status.state] ?? status.state;
  if (!label) return null;
  const colorClass = STATUS_COLOR[status.state] || "text-[#8A8A8A]";
  return (
    <span className={`text-[10px] uppercase tracking-[0.2em] ${colorClass}`} title={status.reason || undefined}>
      {label}{typeof status.progress_pct === "number" ? ` · ${status.progress_pct}%` : ""}
      {status.reason ? ` — ${status.reason}` : ""}
    </span>
  );
}

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
};
const fmtDuration = (s) => {
  if (!s) return "0m";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};
const SEED_STATE = {
  uploading: "Seeding", stalledUP: "Seeding (idle)", forcedUP: "Seeding (forced)", queuedUP: "Seeding (queued)",
  pausedUP: "Paused", stoppedUP: "Stopped", downloading: "Downloading", stalledDL: "Stalled",
  checkingUP: "Checking", checkingDL: "Checking", error: "Error", missingFiles: "Files missing",
};

// Live view of the torrents behind a request (polls qBittorrent every 5 s while open),
// with a button to remove a torrent from qBittorrent (stops seeding).
function SeedingPanel({ requestId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});

  const poll = async () => {
    try {
      const { data: d } = await api.get(`/requests/${requestId}/seeding`);
      setData(d); setErr("");
    } catch (e) { setErr(e.response?.data?.detail || "Could not read qBittorrent"); }
  };
  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [requestId]);

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.name}" from qBittorrent? This stops seeding. The copy in your library is not touched.`)) return;
    const deleteFiles = window.confirm("Also delete this torrent's downloaded files from the download folder?\n\nOK = delete the files too\nCancel = keep the files, only remove the torrent");
    setBusy((p) => ({ ...p, [t.hash]: true }));
    try {
      await api.delete(`/requests/${requestId}/seeding/${t.hash}`, { params: { delete_files: deleteFiles } });
      toast.success("Removed from qBittorrent");
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove the torrent");
    } finally {
      setBusy((p) => ({ ...p, [t.hash]: false }));
    }
  };

  return (
    <div className="mt-2 border border-[#222] bg-[#0A0A0A]" data-testid={`seeding-${requestId}`}>
      {err && <p className="text-xs text-[#fca5a5] px-3 py-2">{err}</p>}
      {!err && data === null && <p className="text-xs text-[#8A8A8A] px-3 py-2">Reading qBittorrent…</p>}
      {data && data.torrents.length === 0 && (
        <p className="text-xs text-[#8A8A8A] px-3 py-2">
          {data.grabbed ? "Not in qBittorrent any more (already removed)." : "Nothing grabbed for this request yet."}
        </p>
      )}
      {data && data.torrents.map((t) => (
        <div key={t.hash} className="flex items-center justify-between gap-4 px-3 py-2 border-b border-[#222] last:border-b-0">
          <div className="min-w-0">
            <p className="text-xs text-white truncate" title={t.name}>{t.name}</p>
            <p className="text-[11px] text-[#8A8A8A] mt-1">
              <span className={t.seeding ? "text-[#86efac]" : ""}>{SEED_STATE[t.state] || t.state}</span>
              {t.progress < 100 ? ` · ${t.progress}%` : ""}
              {` · ratio ${t.ratio} · uploaded ${fmtBytes(t.uploaded)} · ↑ ${fmtBytes(t.upspeed)}/s`}
              {t.progress < 100 ? ` · ↓ ${fmtBytes(t.dlspeed)}/s` : ""}
              {` · seeded ${fmtDuration(t.seeding_time)} · ${t.num_seeds} seeds / ${t.num_leechs} peers · ${fmtBytes(t.size)}`}
            </p>
          </div>
          <button onClick={() => remove(t)} disabled={busy[t.hash]}
            className="shrink-0 text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#fca5a5] hover:text-[#fca5a5] text-[#8A8A8A] px-3 py-2 disabled:opacity-50"
            data-testid={`seeding-remove-${t.hash}`}>{busy[t.hash] ? "Removing…" : "Remove from qBittorrent"}</button>
        </div>
      ))}
    </div>
  );
}

function CandidatePicker({ request, contentType, onClose, onPicked }) {
  const [candidates, setCandidates] = useState(null);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/requests/${request.id}/candidates`, { params: { content_type: contentType } });
        if (!cancelled) setCandidates(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Could not search Radarr/Sonarr");
          onClose();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [request.id, contentType]);

  const pick = async (c) => {
    setPicking(true);
    try {
      await onPicked(c);
    } finally {
      setPicking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div
        className="bg-[#0F0F0F] border border-[#222] max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="candidate-picker"
      >
        <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
          <h3 className="text-white font-display font-bold">
            Pick the right match for "{request.title}"{request.year ? ` (${request.year})` : ""}
          </h3>
          <button onClick={onClose} className="text-[#8A8A8A] hover:text-white text-xs uppercase tracking-[0.2em]">Close</button>
        </div>
        {candidates === null ? (
          <p className="text-[#8A8A8A] text-sm px-5 py-6">Searching…</p>
        ) : candidates.length === 0 ? (
          <p className="text-[#8A8A8A] text-sm px-5 py-6">No results found.</p>
        ) : (
          <div>
            {candidates.map((c) => (
              <button
                key={c.tmdb_id || c.tvdb_id}
                onClick={() => pick(c)}
                disabled={picking}
                className="w-full flex items-start gap-4 px-5 py-4 border-b border-[#222] last:border-b-0 text-left hover:bg-[#1A1A1A] disabled:opacity-50"
                data-testid={`candidate-${c.tmdb_id || c.tvdb_id}`}
              >
                {c.poster_url ? (
                  <img src={c.poster_url} alt="" className="w-12 h-18 object-cover shrink-0 bg-[#1A1A1A]" />
                ) : (
                  <div className="w-12 h-18 shrink-0 bg-[#1A1A1A]" />
                )}
                <div className="min-w-0">
                  <p className="text-white">{c.title} {c.year ? <span className="text-[#8A8A8A]">({c.year})</span> : null}</p>
                  {c.overview && <p className="text-xs text-[#8A8A8A] mt-1 line-clamp-2">{c.overview}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Requests() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [notes, setNotes] = useState("");
  const [approving, setApproving] = useState({});
  const [searching, setSearching] = useState({});
  const [seedOpen, setSeedOpen] = useState({});
  const [picker, setPicker] = useState(null); // { request, contentType }
  const [dlState, setDlState] = useState({}); // requestId -> live download state (for the needs-action highlight)
  const onDlState = (id, st) => setDlState((p) => (p[id] === st ? p : { ...p, [id]: st }));

  const load = async () => {
    const { data } = await api.get("/requests/mine");
    setMine(data);
    if (user?.is_admin) {
      const { data: a } = await api.get("/requests");
      setAll(a);
    }
  };
  useEffect(() => { load(); }, [user?.is_admin]);

  const approveWithCandidate = async (request, contentType, candidate) => {
    const id = request.id;
    setApproving((p) => ({ ...p, [id]: true }));
    try {
      const idField = contentType === "movie" ? { tmdb_id: candidate.tmdb_id } : { tvdb_id: candidate.tvdb_id };
      const { data } = await api.post(`/requests/${id}/approve`, { content_type: contentType, ...idField });
      toast.success(`Searching for "${data.matched_title || data.title}"`);
      setPicker(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve request");
    } finally {
      setApproving((p) => ({ ...p, [id]: false }));
    }
  };

  // "Search again": re-runs Radarr/Sonarr's indexer search for an approved request that never got a download.
  const researchOne = async (r) => {
    setSearching((p) => ({ ...p, [r.id]: true }));
    try {
      const { data } = await api.post(`/requests/${r.id}/research`);
      toast.success(data.state === "downloaded"
        ? `"${r.matched_title || r.title}" is already downloaded`
        : `Searching again for "${r.matched_title || r.title}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the search");
    } finally {
      setSearching((p) => ({ ...p, [r.id]: false }));
    }
  };

  const researchAll = async () => {
    setSearching((p) => ({ ...p, all: true }));
    try {
      // Runs in the background on the server (paced one request at a time); poll until it finishes.
      const { data: started } = await api.post("/requests/research-all");
      toast.success(`Searching for ${started.total} request${started.total === 1 ? "" : "s"}…`);
      for (let misses = 0; ;) {
        await new Promise((r) => setTimeout(r, 2500));
        let j;
        try { ({ data: j } = await api.get(`/jobs/${started.job_id}`)); misses = 0; }
        catch (e) { if (e.response?.status === 404 || ++misses > 5) throw new Error("Lost track of the search job"); continue; }
        if (!j.running) {
          const searched = j.results.filter((r) => r.state === "searching").length;
          const already = j.results.filter((r) => r.state === "downloaded").length;
          const failed = j.results.filter((r) => r.state === "failed").length;
          toast.success(`Searched ${searched}`
            + (already ? ` · ${already} already downloaded` : "")
            + (failed ? ` · ${failed} failed` : ""));
          break;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not start the searches");
    } finally {
      setSearching((p) => ({ ...p, all: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post("/requests", { title, year: year ? Number(year) : null, notes });
      toast.success("Request submitted");
      setTitle(""); setYear(""); setNotes("");
      load();
    } catch {
      toast.error("Could not submit request");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  // "Download anyway" on a duplicate: re-approve with force to bypass the already-in-library check.
  const approveForce = async (r) => {
    setApproving((p) => ({ ...p, [r.id]: true }));
    try {
      const { data } = await api.post(`/requests/${r.id}/approve`, { content_type: r.content_type, force: true });
      toast.success(`Downloading "${data.matched_title || data.title}" anyway`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve");
    } finally {
      setApproving((p) => ({ ...p, [r.id]: false }));
    }
  };

  // Check the current pending requests against the library; flags the ones already owned.
  const scanDupes = async () => {
    setSearching((p) => ({ ...p, dupes: true }));
    try {
      const { data } = await api.post("/requests/dedupe-scan");
      toast.success(`Checked ${data.scanned}, flagged ${data.flagged} already in library`);
      if (data.errors?.length) {
        toast.error(`${data.errors.length} request(s) could not be checked: ${data.errors[0].title} — ${data.errors[0].error}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Duplicate scan failed");
    } finally {
      setSearching((p) => ({ ...p, dupes: false }));
    }
  };

  // Delete/Reject removes the request outright instead of just tagging it "rejected" — otherwise
  // it just sits forever in both My Requests and All Requests. If a download is already underway
  // (approved + Radarr/Sonarr actively grabbing it), confirm first since deleting the request here
  // doesn't cancel that download — the file will still land in the library on its own.
  const deleteReject = async (r) => {
    if (r.status === "approved") {
      let inProgress = false;
      try {
        const { data } = await api.get(`/requests/${r.id}/status`);
        inProgress = ["searching", "queued", "downloading", "partially downloaded", "delay"].includes(data.state);
      } catch { /* couldn't check — fall through to the safe generic confirm below */ }
      if (inProgress) {
        if (!window.confirm(
          `"${r.matched_title || r.title}" still has a download in progress. Deleting this request won't stop it — the file will still land in the library once it finishes. Delete/reject anyway?`
        )) return;
      } else if (!window.confirm(`Delete/reject the request for "${r.matched_title || r.title}"?`)) {
        return;
      }
    } else if (!window.confirm(`Delete/reject the request for "${r.title}"?`)) {
      return;
    }
    try {
      await api.delete(`/requests/${r.id}`);
      toast.success("Request deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="requests-page">
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Library wishlist</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white mt-3">
          Requests
        </h1>
        <p className="text-[#8A8A8A] mt-4 max-w-2xl leading-relaxed">
          Want a film added to the library? Submit a request and the admin will review it. Owned content only.
        </p>
        {user?.is_admin && <ThrottleBar />}

        <form onSubmit={submit} className="mt-12 grid md:grid-cols-3 gap-4 max-w-3xl" data-testid="request-form">
          <div className="md:col-span-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Title</span>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
              data-testid="request-title-input"
              required
            />
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Year</span>
            <input
              type="number" value={year} onChange={(e) => setYear(e.target.value)}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
              data-testid="request-year-input"
            />
          </div>
          <div className="md:col-span-3">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Notes (optional)</span>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
              data-testid="request-notes-input"
            />
          </div>
          <div className="md:col-span-3">
            <button
              type="submit"
              className="bg-[#D9381E] hover:bg-[#ED4B32] text-white px-6 py-3 text-sm uppercase tracking-[0.2em]"
              data-testid="request-submit-button"
            >
              Submit Request
            </button>
          </div>
        </form>

        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold tracking-tight text-white mb-4">My Requests</h2>
          {mine.length === 0 ? (
            <p className="text-[#8A8A8A] text-sm" data-testid="my-requests-empty">No requests yet.</p>
          ) : (
            <div className="border border-[#222]">
              {mine.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-5 py-4 border-b border-[#222] last:border-b-0" data-testid={`my-request-${r.id}`}>
                  <div>
                    <p className="text-white">{r.title} {r.year ? <span className="text-[#8A8A8A]">({r.year})</span> : null}</p>
                    {r.notes && <p className="text-xs text-[#8A8A8A] mt-1">{r.notes}</p>}
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
                    r.status === "fulfilled" ? "text-[#86efac]" :
                    r.status === "rejected" ? "text-[#fca5a5]" :
                    r.status === "approved" ? "text-[#93c5fd]" : "text-[#fcd34d]"
                  }`}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {user?.is_admin && (
          <div className="mt-16">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="font-display text-2xl font-bold tracking-tight text-white">All Requests (Admin)</h2>
              <div className="flex gap-2">
                <button onClick={scanDupes} disabled={searching.dupes}
                  className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#fcd34d] hover:text-[#fcd34d] text-[#8A8A8A] px-3 py-2 disabled:opacity-50"
                  data-testid="scan-dupes">{searching.dupes ? "Checking…" : "Scan for duplicates"}</button>
                {all.some((r) => r.status === "approved") && (
                  <button onClick={researchAll} disabled={searching.all}
                    className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#93c5fd] hover:text-[#93c5fd] text-[#8A8A8A] px-3 py-2 disabled:opacity-50"
                    data-testid="research-all">{searching.all ? "Searching…" : "Search all missing"}</button>
                )}
              </div>
            </div>
            {all.length === 0 ? (
              <p className="text-[#8A8A8A] text-sm">No requests in queue.</p>
            ) : (
              <div className="border border-[#222]">
                {all.map((r) => (
                  <div key={r.id} className={`flex items-center justify-between gap-4 px-5 py-4 border-b border-[#222] last:border-b-0 ${r.status === "approved" && dlState[r.id] === "downloaded" ? "bg-[#fcd34d]/10 border-l-2 border-l-[#fcd34d]" : ""}`} data-testid={`admin-request-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-white truncate">
                        {r.matched_title || r.title} {(r.matched_year || r.year) ? <span className="text-[#8A8A8A]">({r.matched_year || r.year})</span> : null}
                      </p>
                      <p className="text-xs text-[#8A8A8A] mt-1">By {r.user_name || "unknown"} · {r.status}{r.content_type ? ` · ${r.content_type}` : ""}</p>
                      {r.notes && <p className="text-xs text-[#666] mt-1">{r.notes}</p>}
                      {r.status === "approved" && <div className="mt-1"><LiveStatus requestId={r.id} onState={onDlState} /></div>}
                      {r.status === "approved" && dlState[r.id] === "downloaded" && <p className="text-xs text-[#fcd34d] mt-1" data-testid={`ready-note-${r.id}`}>Download complete — ready to mark fulfilled.</p>}
                      {r.status === "duplicate" && <p className="text-xs text-[#fcd34d] mt-1" data-testid={`dup-note-${r.id}`}>Already in library: {r.dup_of_title || r.matched_title || r.title}{r.dup_of_year ? ` (${r.dup_of_year})` : ""} — re-download?</p>}
                      {seedOpen[r.id] && <SeedingPanel requestId={r.id} />}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {r.status === "pending" && (
                        <>
                          <button onClick={() => setPicker({ request: r, contentType: "movie" })} disabled={approving[r.id]}
                            className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#93c5fd] hover:text-[#93c5fd] text-[#8A8A8A] px-3 py-2 disabled:opacity-50"
                            data-testid={`approve-movie-${r.id}`}>Approve (Movie)</button>
                          <button onClick={() => setPicker({ request: r, contentType: "tv" })} disabled={approving[r.id]}
                            className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#93c5fd] hover:text-[#93c5fd] text-[#8A8A8A] px-3 py-2 disabled:opacity-50"
                            data-testid={`approve-tv-${r.id}`}>Approve (TV)</button>
                        </>
                      )}
                      {r.status === "duplicate" && (
                        <>
                          <button onClick={() => approveForce(r)} disabled={approving[r.id]}
                            className="text-[10px] uppercase tracking-[0.2em] border border-[#fcd34d]/40 hover:border-[#fcd34d] text-[#fcd34d] px-3 py-2 disabled:opacity-50"
                            data-testid={`dup-download-${r.id}`}>{approving[r.id] ? "…" : "Download anyway"}</button>
                          <button onClick={() => updateStatus(r.id, "rejected")}
                            className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#fca5a5] hover:text-[#fca5a5] text-[#8A8A8A] px-3 py-2"
                            data-testid={`dup-deny-${r.id}`}>Deny</button>
                        </>
                      )}
                      {(r.radarr_id || r.sonarr_id) && (
                        <button onClick={() => setSeedOpen((p) => ({ ...p, [r.id]: !p[r.id] }))}
                          className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-2 ${seedOpen[r.id] ? "border-[#86efac] text-[#86efac]" : "border-[#222] hover:border-[#86efac] hover:text-[#86efac] text-[#8A8A8A]"}`}
                          data-testid={`seeding-toggle-${r.id}`}>Seeding</button>
                      )}
                      {r.status === "approved" && (
                        <button onClick={() => researchOne(r)} disabled={searching[r.id]}
                          className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#93c5fd] hover:text-[#93c5fd] text-[#8A8A8A] px-3 py-2 disabled:opacity-50"
                          data-testid={`research-${r.id}`}>{searching[r.id] ? "Searching…" : "Search again"}</button>
                      )}
                      <button onClick={() => updateStatus(r.id, "fulfilled")}
                        className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#86efac] hover:text-[#86efac] text-[#8A8A8A] px-3 py-2"
                        data-testid={`fulfill-${r.id}`}>Fulfilled</button>
                      <button onClick={() => deleteReject(r)}
                        className="text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#fca5a5] hover:text-[#fca5a5] text-[#8A8A8A] px-3 py-2"
                        data-testid={`reject-${r.id}`}>Delete/Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {user?.is_admin && <DecadeBrowser onAdded={load} />}
      </div>
      {picker && (
        <CandidatePicker
          request={picker.request}
          contentType={picker.contentType}
          onClose={() => setPicker(null)}
          onPicked={(c) => approveWithCandidate(picker.request, picker.contentType, c)}
        />
      )}
    </div>
  );
}
